import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";

type BillingCategory = {
    id?: number;
    name?: string;
    glCode?: string;
};

type BillingCategoryModalProps = {
    isOpen: boolean;
    category: BillingCategory | null;
    onClose: () => void;
    updateSpinner: (isLoading: boolean) => void;
};

type BillingCategoryErrors = {
    name?: string | null;
};

export default function BillingCategoryModal({ isOpen, category, onClose, updateSpinner }: BillingCategoryModalProps) {
    const [formData, updateFormData] = useState<BillingCategory | null>(null);
    const [errors, updateErrors] = useState<BillingCategoryErrors>({});
    const [originalCategory, setOriginalCategory] = useState<BillingCategory | null>(null);
    useEffect(() => {
        if (isOpen) {
            setOriginalCategory(category);
            updateFormData(category);
        }
    }, [isOpen, category]);

    if (!isOpen) return null;

    const footer = (
        <>
            <div className="footerLeft">
                {originalCategory && originalCategory.id > 0 && (
                    <button
                        type="button"
                        className="btn btn-danger iconButton"
                        onClick={handleCancel}
                        title="Delete Billing Category"
                    >
                        <MdDelete />
                    </button>
                )}
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className={`btn btn-primary${!isChanged() || !formData?.name?.trim() ? ' disabled' : ''}`}
                    onClick={handleSave}
                    disabled={!isChanged() || !formData?.name?.trim()}
                >
                    Save
                </button>
            </div>
        </>
    );

    const fields: Record<string, ReactNode> = {
        "Category Name *": <input id="categoryName" type="text" value={formData?.name || ''} onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)} placeholder="Enter category name" className={errors.name ? 'error' : ''} />,
        ...(errors.name && { '': <span className="errorText">{errors.name}</span> }),
        "GL Code": <input id="glCode" type="text" value={formData?.glCode || ''} onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('glCode', e.target.value)} placeholder="Enter GL code (optional)" />,
    };

    function validateForm() {
        const newErrors: BillingCategoryErrors = {};
        if (!formData?.name?.trim()) 
            newErrors.name = "Category name is required";
        updateErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function isChanged() {
        if (!formData) return false;
        if (originalCategory && originalCategory.id) {
            return (
                formData.name !== originalCategory.name ||
                formData.glCode !== originalCategory.glCode
            );
        }
        return Boolean(formData.name?.trim()) || Boolean(formData.glCode?.trim());
    }

    async function handleSave() {
        if (!validateForm() || !isChanged()) return;
        updateSpinner(true);
        try {
            let resp = await RightTimeApi.sendAsync(`/BillingCategory`, formData);
            if (resp && resp.ok) {
                onClose();
            } else {
                toast.error("Failed to save billing category.");
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Error saving billing category.";
            toast.error(message || "Error saving billing category.");
        } finally {
            updateSpinner(false);
        }
    }

    function handleCancel() {
        updateFormData(null);
        updateErrors({});
        onClose();
    }

    function handleInputChange(field: keyof BillingCategory, value: string | boolean) {
        updateFormData(prev => ({
            ...prev,
            [field]: value
        } as BillingCategory));
        if (errors[field]) {
            updateErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            title={originalCategory && originalCategory.id ? 'Edit Billing Category' : 'Add New Billing Category'}
            onClose={handleCancel}
            footer={footer}
            className="billingCategoryModal"
        >
            <div className="modalBody">
                <ModalFormFields fields={fields} />
            </div>
        </Modal>
    );
}
